/**
 * Collection Referrer Tracking
 * 
 * This script handles collection referrer tracking for product breadcrumbs.
 * It stores the referring collection in sessionStorage and:
 * 1. Attaches click handlers to collection product links
 * 2. Updates product URLs with collection query parameters
 * 3. Reads query parameters for breadcrumb rendering
 */

(() => {
  // Initialize when DOM content is loaded
  document.addEventListener('DOMContentLoaded', () => {
    // Track collection clicks
    trackCollectionClicks();
    
    // Track product to product links (for related products)
    trackProductToProductLinks();
    
    // For product pages, update breadcrumbs based on query param or referrer
    if (window.location.pathname.includes('/products/')) {
      updateBreadcrumbsFromReferrer();
    }
  });
  
  /**
   * Attach click handlers to product links in collections
   */
  function trackCollectionClicks() {
    // Only run this on collection pages
    if (!window.location.pathname.includes('/collections/')) return;
    
    // Get current collection handle from URL
    const collectionMatch = window.location.pathname.match(/\/collections\/([^\/]+)/);
    if (!collectionMatch) return;
    
    const collectionHandle = collectionMatch[1];
    const collectionTitle = document.querySelector('.collection-title, h1')?.textContent.trim();
    
    // Store collection info as we'll need both the handle and title
    if (collectionHandle && collectionTitle) {
      const collectionInfo = {
        handle: collectionHandle,
        title: collectionTitle
      };
      
      // Find all product links in the current collection and modify them
      document.querySelectorAll('a[href*="/products/"]').forEach(link => {
        // Only process links that point to products
        if (link.href.includes('/products/')) {
          // Add collection parameter to product URLs
          const productUrl = new URL(link.href);
          productUrl.searchParams.set('collection', collectionHandle);
          link.href = productUrl.toString();
          
          // Also store the collection info for this product in session storage
          link.addEventListener('click', () => {
            const productHandle = link.href.match(/\/products\/([^\/\?]+)/)?.[1];
            if (productHandle) {
              sessionStorage.setItem(`collection_referrer_${productHandle}`, JSON.stringify(collectionInfo));
            }
          });
        }
      });
    }
  }
  
  /**
   * Attach handlers to product links on product pages (related products, etc.)
   */
  function trackProductToProductLinks() {
    // Only run this on product pages
    if (!window.location.pathname.includes('/products/')) return;
    
    // Get current product handle from URL
    const productHandle = window.location.pathname.match(/\/products\/([^\/]+)/)?.[1];
    if (!productHandle) return;
    
    // Get the current collection context from breadcrumb
    const collectionLink = document.querySelector('.breadcrumbs-list a[href*="/collections/"]');
    if (!collectionLink) return;
    
    const collectionMatch = collectionLink.href.match(/\/collections\/([^\/]+)/);
    if (!collectionMatch) return;
    
    const collectionHandle = collectionMatch[1];
    const collectionTitle = collectionLink.textContent.trim();
    
    // Store collection info
    const collectionInfo = {
      handle: collectionHandle,
      title: collectionTitle
    };
    
    // Find all product links on the page and modify them
    document.querySelectorAll('a[href*="/products/"]').forEach(link => {
      // Skip links to the current product
      const linkProductHandle = link.href.match(/\/products\/([^\/\?]+)/)?.[1];
      if (!linkProductHandle || linkProductHandle === productHandle) return;
      
      // Add collection parameter to product URLs
      const productUrl = new URL(link.href);
      productUrl.searchParams.set('collection', collectionHandle);
      link.href = productUrl.toString();
      
      // Also store the collection info for this product in session storage
      link.addEventListener('click', () => {
        sessionStorage.setItem(`collection_referrer_${linkProductHandle}`, JSON.stringify(collectionInfo));
      });
    });
  }
  
  /**
   * Update breadcrumbs based on the referrer or query parameter
   */
  function updateBreadcrumbsFromReferrer() {
    // Check URL for collection parameter
    const urlParams = new URLSearchParams(window.location.search);
    const collectionParam = urlParams.get('collection');
    
    // Get product handle from URL
    const productHandle = window.location.pathname.match(/\/products\/([^\/]+)/)?.[1];
    if (!productHandle) return;
    
    // Priority 1: Use collection from URL parameter
    if (collectionParam) {
      fetchCollectionInfo(collectionParam, (collectionInfo) => {
        updateBreadcrumb(collectionInfo);
        // Also store this for future reference
        sessionStorage.setItem(`collection_referrer_${productHandle}`, JSON.stringify(collectionInfo));
      });
      return;
    }
    
    // Priority 2: Check session storage for previous collection
    const storedCollection = sessionStorage.getItem(`collection_referrer_${productHandle}`);
    if (storedCollection) {
      try {
        const collectionInfo = JSON.parse(storedCollection);
        updateBreadcrumb(collectionInfo);
        return;
      } catch (e) {
        console.error('Error parsing stored collection info:', e);
      }
    }
    
    // Priority 3: Check document referrer if coming from a collection
    const referrer = document.referrer;
    if (referrer && referrer.includes('/collections/')) {
      const referrerMatch = referrer.match(/\/collections\/([^\/]+)/);
      if (referrerMatch) {
        const collectionHandle = referrerMatch[1];
        fetchCollectionInfo(collectionHandle, (collectionInfo) => {
          updateBreadcrumb(collectionInfo);
          // Store for future reference
          sessionStorage.setItem(`collection_referrer_${productHandle}`, JSON.stringify(collectionInfo));
        });
        return;
      }
    }
    
    // If we get here, we'll use default breadcrumb behavior (first collection)
    console.log('Using default collection breadcrumb');
  }
  
  /**
   * Fetch collection info by handle
   */
  function fetchCollectionInfo(collectionHandle, callback) {
    // First try to see if we have the collection info in the page
    const collectionTitle = document.querySelector(`a[href*="/collections/${collectionHandle}"]`)?.textContent.trim();
    
    if (collectionTitle) {
      callback({
        handle: collectionHandle,
        title: collectionTitle
      });
      return;
    }
    
    // If not found in page, we need to fetch the collection title
    fetch(`/collections/${collectionHandle}?view=title-only`)
      .then(response => response.text())
      .then(html => {
        // Extract title from response
        const titleMatch = html.match(/<title>(.*?)<\/title>/);
        const title = titleMatch ? titleMatch[1].replace(' – Your Store', '') : collectionHandle;
        
        callback({
          handle: collectionHandle,
          title: title
        });
      })
      .catch(error => {
        console.error('Error fetching collection info:', error);
        // Use handle as fallback title
        callback({
          handle: collectionHandle, 
          title: collectionHandle.replace(/-/g, ' ')
        });
      });
  }
  
  /**
   * Update breadcrumb with collection info
   */
  function updateBreadcrumb(collectionInfo) {
    // Find the collection link in breadcrumbs
    const breadcrumbsElement = document.querySelector('.breadcrumbs-list');
    if (!breadcrumbsElement) return;
    
    const collectionLink = breadcrumbsElement.querySelector('a[href*="/collections/"]');
    if (collectionLink) {
      // Update href and text content
      collectionLink.href = `/collections/${collectionInfo.handle}`;
      collectionLink.textContent = collectionInfo.title;
      
      // Also update any structured data
      const structuredData = document.querySelector('script[type="application/ld+json"]');
      if (structuredData) {
        try {
          const data = JSON.parse(structuredData.textContent);
          // Look for collection item in breadcrumb data
          if (data && data.itemListElement) {
            for (let i = 0; i < data.itemListElement.length; i++) {
              const item = data.itemListElement[i];
              if (item.item && item.item.includes('/collections/')) {
                // Update the structured data
                item.name = collectionInfo.title;
                item.item = window.location.origin + `/collections/${collectionInfo.handle}`;
              }
            }
            // Update the script content
            structuredData.textContent = JSON.stringify(data);
          }
        } catch (e) {
          console.error('Error updating structured data:', e);
        }
      }
    }
  }
})(); 